import { FC, memo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router';
import { Share2 } from 'lucide-react';

import { APP_ROUTES } from '@navigation/types';
import { useScreenSizes } from '@hooks/useScreenSizes';
import { useShareModal } from '@hooks/useShareModal';
import { useDownloadAppModal } from '@hooks/useDownloadAppModal';
import { useShowDataById } from '@redux/selectors/shows';
import { useAuthUser } from '@redux/selectors/auth';
import { theme } from 'theme';
import { ShowType } from '@typings/show';
import { generateShowShareData } from '@utils/share';
import ChannelView from '@components/show/menu/components/ChannelView';
import ExitButton from '@components/show/menu/components/ExitButton';
import LogoButton from '@components/show/menu/components/LogoButton';
import Loader from '@components/loader';
import Participants from '@components/show/participants';
import Sides from '@components/show/card/components/Sides';
import { PremierText } from '@components/text';
import backgroundImage from '@assets/images/card.png';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.black};
`;

const LoaderContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Header = styled.div<{ $backgroundImage?: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  background-image: ${({ $backgroundImage }) =>
    $backgroundImage ? `url(${$backgroundImage})` : 'none'};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ theme }) =>
      `${theme.colors.black}${theme.opacity[60]}`};
    z-index: 1;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const MenuContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  gap: 8%;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    justify-content: flex-start;
  }
`;

const Menu = styled.div`
  min-width: 50%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 20%;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex: 1;
    justify-content: space-between;
  }
`;

const TitleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: 8px 16px;
  gap: 32px;
`;

const Title = styled(PremierText)`
  flex: 1;
  font-size: ${({ theme }) => theme.fonts.fontSize['2xl']};
  color: ${({ theme }) => theme.colors.white};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fonts.fontSize.xl};
  }
`;

const ShareButton = styled(Share2)`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 20px;
    height: 20px;
  }
`;

const Body = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
`;

const Content = styled.div`
  position: relative;
  width: 60%;
  height: calc(100% - 96px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
    height: 100%;
  }
`;

const BottomMenu = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
`;

const ScheduledShow: FC = () => {
  const { id } = useParams();
  const show = useShowDataById(id);
  const { user, isAnonymous } = useAuthUser();
  const navigate = useNavigate();
  const { isMobile } = useScreenSizes();
  const { openShareModal } = useShareModal();
  const { openDownloadAppModal } = useDownloadAppModal();

  useEffect(() => {
    const isHost = show?.hostId && !isAnonymous && user?.id === show.hostId;

    if (!isHost) {
      navigate(isAnonymous ? APP_ROUTES.HOME : APP_ROUTES.SEARCH, {
        replace: true,
      });
    }
  }, [show, user, isAnonymous, navigate]);

  const onExitShow = useCallback(() => {
    navigate(APP_ROUTES.HOME);
  }, [navigate]);

  const onSharePress = useCallback(() => {
    if (show) {
      const shareData = generateShowShareData({
        id: show.id,
        status: show.status,
        title: show.title,
        image: show.image,
      });
      openShareModal(shareData);
    }
  }, [show, openShareModal]);

  const onStartShow = useCallback(() => {
    openDownloadAppModal();
  }, [openDownloadAppModal]);

  if (!show || !show.hostId || user?.id !== show.hostId) {
    return (
      <LoaderContainer>
        <Loader size='large' />
      </LoaderContainer>
    );
  }

  return (
    <Container>
      <Header $backgroundImage={show?.image || backgroundImage}>
        <MenuContainer>
          <Menu>
            {!!show?.channelId && <ChannelView channelId={show.channelId} />}
          </Menu>

          <ExitButton text='Exit' onExitShow={onExitShow} />
        </MenuContainer>

        <TitleContainer>
          <Title>{show?.title || ''}</Title>

          <ShareButton onClick={onSharePress} />
        </TitleContainer>
      </Header>

      <Body>
        <Content>
          {show.type === ShowType.Sideless ? null : (
            <Sides
              blueSide={show.blueSide}
              redSide={show.redSide}
              isCard={false}
              textStyle={{
                fontSize: isMobile
                  ? theme.fonts.fontSize.sm
                  : theme.fonts.fontSize.lg,
                paddingBlock: '4px',
              }}
            />
          )}

          <Participants
            showId={id || ''}
            leftSpeakerId={null}
            rightSpeakerId={null}
            speakingUIDs={{}}
            isStatusesShown={false}
            isRecord={false}
          />
        </Content>

        <BottomMenu>
          <LogoButton text='Start Show' onClick={onStartShow} />
        </BottomMenu>
      </Body>
    </Container>
  );
};

export default memo(ScheduledShow);
